import React, { useState, useEffect } from "react";
import AutoGridLayout from "../../../../components/ModuleCommon/FormBuilder/AutoGridLayout";
import FieldInput from "../../../../components/ModuleCommon/FormBuilder/FieldInput";
import FieldDropdown from "../../../../components/ModuleCommon/FormBuilder/FieldDropdown";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";

const FormInventory = () => {
  const token = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCode");

  // console.log(token);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const [inventoryData, setInventoryData] = useState([
    { id: "sku", label: "SKU", type: "text", value: "" },
    { id: "name", label: "Name", type: "text", value: "" },
    {
      id: "category",
      label: "Category",
      type: "dropdown",
      value: "",
      options: [],
    },
    { id: "description", label: "Description", type: "textarea", value: "" },
    {
      id: "costPrice",
      label: "Cost Price",
      type: "group",
      components: [
        { id: "costValue", type: "text", placeholder: "Amount", value: "" },
        {
          id: "costCurrency",
          type: "dropdown",
          value: "$",
          options: ["$", "€", "₹", "£"],
        },
      ],
    },
    {
      id: "sellingPrice",
      label: "Selling Price",
      type: "group",
      components: [
        { id: "sellingValue", type: "text", placeholder: "Amount", value: "" },
        {
          id: "sellingCurrency",
          type: "dropdown",
          value: "$",
          options: ["$", "€", "₹", "£"],
        },
      ],
    },
    { id: "quantity", label: "Quantity", type: "number", value: "" },
    { id: "reorderLevel", label: "Reorder Level", type: "number", value: "" },
    {
      id: "location",
      label: "Location",
      type: "dropdown",
      value: "",
      options: [],
    },
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // FETCH CATEGORY & LOCATION OPTIONS
  // ─────────────────────────────────────────────────────────────────────────────

  // New category state (expanded for additional fields)
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    active: true, // Default to true
  });

  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

  // New location state
  const [newLocation, setNewLocation] = useState({
    locationCode: "",
    capacity: "",
    inventoryLevels: "",
  });
  const [showNewLocationInput, setShowNewLocationInput] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      if (!token) {
        console.error("Token is missing. Cannot fetch dropdown options.");
        return;
      }

      try {
        const [categoryResponse, locationResponse] = await Promise.allSettled([
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/product-categories/by-organization`,
            {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "Office-Code": officeCode,
              },
            }
          ),
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/storage-locations/by-organization`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "Office-Code": officeCode,
              },
            }
          ),
        ]);

        // ✅ Handle Category Data (Even if Location Fails)
        if (
          categoryResponse.status === "fulfilled" &&
          categoryResponse.value.ok
        ) {
          const categoryData = await categoryResponse.value.json();
          const safeCategoryData = Array.isArray(categoryData)
            ? categoryData
            : [];

          const categoryOptionsList =
            safeCategoryData.length > 0
              ? safeCategoryData.map((c) => ({ value: c.id, label: c.name }))
              : [
                  {
                    value: "no-categories",
                    label: "No Categories Available",
                    disabled: true,
                  },
                ];

          categoryOptionsList.push({
            value: "new",
            label: "➕ Create New Category",
          });
          setCategoryOptions(categoryOptionsList);
        } else {
          console.error("Failed to fetch categories");
          setCategoryOptions([
            {
              value: "no-categories",
              label: "No Categories Available",
              disabled: true,
            },
          ]);
        }

        // ✅ Handle Location Data (Even if Category Fails)
        if (
          locationResponse.status === "fulfilled" &&
          locationResponse.value.ok
        ) {
          const locationData = await locationResponse.value.json();
          const safeLocationData = Array.isArray(locationData)
            ? locationData
            : [];

          const locationOptionsList =
            safeLocationData.length > 0
              ? safeLocationData.map((l) => ({
                  value: l.id,
                  label: l.locationCode,
                }))
              : [
                  {
                    value: "no-locations",
                    label: "No Locations Available",
                    disabled: true,
                  },
                ];

          locationOptionsList.push({
            value: "new",
            label: "➕ Create New Location",
          });

          setLocationOptions(locationOptionsList);
        } else {
          console.error("Failed to fetch locations");
          setLocationOptions([
            {
              value: "no-locations",
              label: "No Locations Available",
              disabled: true,
            },
          ]);
        }

        // ✅ Update inventory fields separately (only update available options)
        setInventoryData((prevData) =>
          prevData.map((field) => {
            if (field.id === "category") {
              return { ...field, options: categoryOptions };
            }
            if (field.id === "location") {
              return { ...field, options: locationOptions };
            }
            return field;
          })
        );
      } catch (error) {
        console.error("Unexpected error fetching dropdown options:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [token, officeCode]);

  // useEffect(() => {
  //   const fetchOptions = async () => {

  //     try {
  //       const [categoryResponse, locationResponse] = await Promise.all([
  //         fetch(`${process.env.REACT_APP_API_BASE_URL}/api/product-categories/by-organization/${officeCode}`, {
  //           headers: { Authorization: `Bearer ${token}` },
  //         }),
  //         fetch(`${process.env.REACT_APP_API_BASE_URL}/api/storage-locations/by-organization/${officeCode}`, {
  //           headers: { Authorization: `Bearer ${token}` },
  //         }),
  //       ]);

  //       if (!categoryResponse.ok || !locationResponse.ok) {
  //         throw new Error("Failed to fetch dropdown options");
  //       }

  //       const [categoryData, locationData] = await Promise.all([
  //         categoryResponse.json(),
  //         locationResponse.json(),
  //       ]);

  //       // ✅ Ensure categoryData & locationData are always arrays
  //       const safeCategoryData = Array.isArray(categoryData) ? categoryData : [];
  //       const safeLocationData = Array.isArray(locationData) ? locationData : [];

  //       // ✅ Ensure empty categories are handled properly
  //       const categoryOptionsList =
  //         safeCategoryData.length > 0
  //           ? safeCategoryData.map((c) => ({ value: c.id, label: c.name }))
  //           : [{ value: "no-categories", label: "No Categories Available", disabled: true }];

  //       categoryOptionsList.push({ value: "new", label: "➕ Create New Category" }); // Add 'Create New' option

  //       console.log("category data: " + safeCategoryData);

  //       setCategoryOptions(categoryOptionsList);

  //       const locationOptionsList =
  //         safeLocationData.length > 0
  //           ? safeLocationData.map((l) => ({ value: l.id, label: l.locationCode }))
  //           : [{ value: "no-locations", label: "No Locations Available", disabled: true }];

  //       setLocationOptions(locationOptionsList);

  //       setInventoryData((prevData) =>
  //         prevData.map((field) => {
  //           if (field.id === "category") {
  //             return { ...field, options: categoryOptionsList };
  //           }
  //           if (field.id === "location") {
  //             return { ...field, options: locationOptionsList };
  //           }
  //           return field;
  //         })
  //       );
  //     } catch (error) {
  //       console.error("Error fetching dropdown options:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchOptions();
  // }, [token, officeCode]); // ✅ Added officeCode as a dependency

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLE CATEGORY SELECTION  AND LOCATION / CREATION
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCategoryChange = async (value) => {
    if (value === "new") {
      setShowNewCategoryInput(true);
      return;
    }
    setShowNewCategoryInput(false);
    handleInventoryChange("category", value);
  };

  const handleLocationChange = async (value) => {
    if (value === "new") {
      setShowNewLocationInput(true);
      return;
    }
    setShowNewLocationInput(false);
    handleInventoryChange("location", value);
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      alert("Please enter a valid category name.");
      return;
    }
    if (!newCategory.description.trim()) {
      alert("Please enter a valid description.");
      return;
    }

    console.log("Sending category payload:", {
      name: newCategory.name,
      description: newCategory.description,
      active: newCategory.active,
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/product-categories`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": officeCode,
          },
          body: JSON.stringify({
            name: newCategory.name,
            description: newCategory.description,
            active: newCategory.active,
            officeCode: officeCode,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to create category");

      const createdCategory = await response.json();
      const updatedCategories = [
        ...categoryOptions.slice(0, -1), // Remove 'Create New' option temporarily
        { value: createdCategory.id, label: createdCategory.name },
        { value: "new", label: "➕ Create New Category" }, // Re-add 'Create New' option
      ];

      setCategoryOptions(updatedCategories);
      setNewCategory({ name: "", description: "", active: true });
      setShowNewCategoryInput(false);
      handleInventoryChange("category", createdCategory.id); // Select the new category
    } catch (error) {
      console.error("Error creating category:", error);
      alert("Failed to create category.");
    }
  };

  const handleCreateLocation = async () => {
    if (!newLocation.locationCode.trim()) {
      alert("Please enter a valid location code.");
      return;
    }
    if (
      !newLocation.capacity ||
      isNaN(newLocation.capacity) ||
      newLocation.capacity < 0
    ) {
      alert("Please enter a valid positive number for capacity.");
      return;
    }
    if (
      !newLocation.inventoryLevels ||
      isNaN(newLocation.inventoryLevels) ||
      newLocation.inventoryLevels < 0
    ) {
      alert("Please enter a valid positive number for inventory levels.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/storage-locations`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": officeCode,
          },
          body: JSON.stringify({
            locationCode: newLocation.locationCode,
            officeCode,
            capacity: Number(newLocation.capacity),
            inventoryLevels: Number(newLocation.inventoryLevels),
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to create location");

      const createdLocation = await response.json();
      const updatedLocations = [
        ...locationOptions.slice(0, -1), // Remove 'Create New' option temporarily
        { value: createdLocation.id, label: createdLocation.locationCode },
        { value: "new", label: "➕ Create New Location" }, // Re-add 'Create New' option
      ];

      setLocationOptions(updatedLocations);
      setNewLocation({ locationCode: "", capacity: "", inventoryLevels: "" });
      setShowNewLocationInput(false);
      handleInventoryChange("location", createdLocation.id); // Select the new location
    } catch (error) {
      console.error("Error creating location:", error);
      alert("Failed to create location.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLE FORM INPUT CHANGES
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInventoryChange = (id, newValue, parentId = null) => {
    setInventoryData((prev) =>
      prev.map((field) => {
        if (parentId) {
          if (field.id === parentId) {
            return {
              ...field,
              components: field.components.map((component) =>
                component.id === id
                  ? { ...component, value: newValue }
                  : component
              ),
            };
          }
          return field;
        } else {
          return field.id === id ? { ...field, value: newValue } : field;
        }
      })
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLE SUBMIT
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const formattedData = inventoryData.reduce((acc, field) => {
      if (field.type === "group") {
        field.components.forEach((component) => {
          acc[component.id] = component.value;
        });
      } else {
        acc[field.id] = field.value;
      }
      return acc;
    }, {});

    const categoryId = parseInt(formattedData.category, 10) || null;
    const storageLocationId = parseInt(formattedData.location, 10) || null; // ✅ Extract Storage Location ID
    const quantity = parseInt(formattedData.quantity, 10) || 0; // ✅ Quantity for storage

    const productRequest = {
      sku: formattedData.sku,
      name: formattedData.name,
      description: formattedData.description,
      costPrice: parseFloat(formattedData.costValue || 0),
      sellingPrice: parseFloat(formattedData.sellingValue || 0),
      reorderLevel: parseInt(formattedData.reorderLevel, 10) || 0,
      // barCode: formattedData.barCode || "",        
      // qrCode: formattedData.qrCode || "",           
      supplierCode: "Global Textiles Ltd.",         // TODO: placeholder; replace as needed
      categoryId: categoryId|| null,
    };
    

    // console.log("🟢 Submitting Product Data:", productRequest);

    try {
      // 1️⃣ First, create the product
      const productResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/products`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": officeCode, // ✅ Ensuring officeCode is passed
          },
          body: JSON.stringify(productRequest),
        }
      );

      if (!productResponse.ok) {
        throw new Error(`API error! Status: ${productResponse.status}`);
      }

      const newProduct = await productResponse.json();
      // console.log("✅ Product Created:", newProduct);

      // 2️⃣ Second, create a product storage entry
      if (storageLocationId) {
        const productStorageRequest = {
          productId: newProduct.id,
          storageLocationId,
          quantity, // ✅ Attach quantity to storage
        };

        console.log(
          "🟢 Submitting Product Storage Data:",
          productStorageRequest
        );

        const storageResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/product-storages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "Office-Code": officeCode,
            },
            body: JSON.stringify(productStorageRequest),
          }
        );

        if (!storageResponse.ok) {
          throw new Error(`API error! Status: ${storageResponse.status}`);
        }

        const newProductStorage = await storageResponse.json();
        console.log("✅ Product Storage Created:", newProductStorage);
      }

      // ✅ Reset the form fields
      setInventoryData((prevData) =>
        prevData.map((field) => ({
          ...field,
          value:
            field.type === "group"
              ? field.components.map((c) => ({ ...c, value: "" }))
              : "",
        }))
      );

      alert("Item Successfully Created.");
      navigate("/dashboard/inventory");
    } catch (error) {
      console.error("❌ Error submitting inventory data:", error);
      alert("Error when creating inventory");
    }
  };

  return (
    <div className="w-full bg-white rounded-md shadow-lg p-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        Add Inventory Item
      </h2>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <AutoGridLayout fields={inventoryData}>
          {/* SKU and Name */}
          <div className="grid grid-cols-2 gap-4">
            {inventoryData.slice(0, 2).map((field) => (
              <div key={field.id} className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  {field.label}
                </label>
                <FieldInput
                  type={field.type}
                  value={field.value}
                  onChange={(val) => handleInventoryChange(field.id, val)}
                />
              </div>
            ))}
          </div>

          {/* Category and Description */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-1">
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Category
              </label>
              <FieldDropdown
                value={inventoryData[2].value}
                options={categoryOptions}
                onChange={handleCategoryChange}
              />
              {showNewCategoryInput && (
                <div className="flex flex-col mt-2 space-y-2">
                  {/* Category Name */}
                  <FieldInput
                    type="text"
                    value={newCategory.name}
                    onChange={(val) =>
                      setNewCategory({ ...newCategory, name: val })
                    }
                    placeholder="Enter category name"
                  />

                  {/* Category Description */}
                  <FieldInput
                    type="text"
                    value={newCategory.description}
                    onChange={(val) =>
                      setNewCategory({ ...newCategory, description: val })
                    }
                    placeholder="Enter category description"
                  />

                  {/* Active Toggle */}
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={newCategory.active}
                      onChange={() =>
                        setNewCategory((prev) => ({
                          ...prev,
                          active: !prev.active,
                        }))
                      }
                    />
                    <span>Active</span>
                  </label>

                  {/* Buttons */}
                  <div className="flex space-x-2">
                    <Button text="Save" onClick={handleCreateCategory} />
                    <Button
                      text="Cancel"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategory({
                          name: "",
                          description: "",
                          active: true,
                        });
                      }}
                      className="bg-gray-500 text-white"
                    />
                  </div>
                </div>
              )}

              {/* <label className="block text-gray-700 text-sm font-medium mb-1">{inventoryData[2].label}</label>
              {showNewCategoryInput && (
                <div className="flex mt-2">
                  <FieldInput type="text" value={newCategory} onChange={setNewCategory} placeholder="Enter category name" />
                  <Button text="Save" onClick={handleCreateCategory} className="ml-2" />
                </div>
              )}
              <FieldDropdown
                value={inventoryData[2].value}
                options={categoryOptions.map((opt) => ({ value: opt.id, label: opt.name }))}
                onChange={(val) => handleInventoryChange(inventoryData[2].id, val)}
              /> */}
            </div>
            <div className="col-span-1">
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Storage Location
              </label>
              <FieldDropdown
                value={inventoryData[8].value}
                options={locationOptions}
                onChange={handleLocationChange}
              />
              {showNewLocationInput && (
                <div className="flex flex-col mt-2 space-y-2">
                  {/* Location Code */}
                  <FieldInput
                    type="text"
                    value={newLocation.locationCode}
                    onChange={(val) =>
                      setNewLocation({ ...newLocation, locationCode: val })
                    }
                    placeholder="Enter location code"
                  />

                  {/* Capacity */}
                  <FieldInput
                    type="number"
                    value={newLocation.capacity}
                    onChange={(val) =>
                      setNewLocation({ ...newLocation, capacity: val })
                    }
                    placeholder="Enter capacity"
                  />

                  {/* Inventory Levels */}
                  <FieldInput
                    type="number"
                    value={newLocation.inventoryLevels}
                    onChange={(val) =>
                      setNewLocation({ ...newLocation, inventoryLevels: val })
                    }
                    placeholder="Enter inventory levels"
                  />

                  {/* Buttons */}
                  <div className="flex space-x-2">
                    <Button text="Save" onClick={handleCreateLocation} />
                    <Button
                      text="Cancel"
                      onClick={() => {
                        setShowNewLocationInput(false);
                        setNewLocation({
                          locationCode: "",
                          capacity: "",
                          inventoryLevels: "",
                        });
                      }}
                      className="bg-gray-500 text-white"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Cost Price & Selling Price */}
          <div className="grid grid-cols-2 gap-4">
            {[inventoryData[4], inventoryData[5]].map((field) => (
              <div key={field.id} className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  {field.label}
                </label>
                <div className="flex space-x-2">
                  {field.components.map((component) =>
                    component.type === "text" ? (
                      <FieldInput
                        key={component.id}
                        type="text"
                        placeholder={component.placeholder}
                        value={component.value}
                        onChange={(val) =>
                          handleInventoryChange(component.id, val, field.id)
                        }
                      />
                    ) : (
                      <FieldDropdown
                        key={component.id}
                        value={component.value}
                        options={component.options}
                        onChange={(val) =>
                          handleInventoryChange(component.id, val, field.id)
                        }
                      />
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quantity, Reorder Level & Location */}
          <div className="grid grid-cols-2 gap-4">
            {inventoryData.slice(6, 8).map((field) => (
              <div key={field.id} className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  {field.label}
                </label>
                {field.type === "dropdown" ? (
                  <FieldDropdown
                    value={field.value}
                    options={locationOptions}
                    onChange={(val) => handleInventoryChange(field.id, val)}
                  />
                ) : (
                  <FieldInput
                    type="text"
                    value={field.value}
                    onChange={(val) => handleInventoryChange(field.id, val)}
                  />
                )}
              </div>
            ))}
          </div>
        </AutoGridLayout>
      )}

      <div className="mt-6 mb-6">
        <Button
          text="Submit"
          onClick={handleSubmit}
          aria-label="Submit"
        />
      </div>
    </div>
  );
};

export default FormInventory;
